import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Loader2, Mail, Phone, Building2, CalendarCheck,
  MessageSquare, Send, User as UserIcon, Activity, Tag, Briefcase,
} from "lucide-react";
import { formatApiError } from "./AuthContext";
import { StagePill } from "./AdminDashboard";
import TasksSection from "./TasksSection";
import SmsSection from "./SmsSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [posting, setPosting] = useState(false);
  const [stageSaving, setStageSaving] = useState(false);
  const [converting, setConverting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const r = await axios.get(`${API}/admin/leads/${id}`);
      setLead(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/admin/leads/${id}/notes`, { body: note.trim() });
      setNote("");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setPosting(false);
    }
  };

  const changeStage = async (newStage) => {
    setStageSaving(true);
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { stage: newStage });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setStageSaving(false);
    }
  };

  const convertToClient = async () => {
    setConverting(true);
    try {
      await axios.post(`${API}/admin/leads/${id}/convert`);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-mid-neutral">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading lead…
      </div>
    );
  }

  if (error && !lead) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
        <div className="mt-2">
          <Link to="/admin/leads" className="text-forest font-semibold underline">Back to leads</Link>
        </div>
      </div>
    );
  }

  if (!lead) return null;

  const stages = [
    "New", "Contacted", "Discovery Call Booked",
    "Qualified", "Proposal", "Won", "Lost",
  ];

  return (
    <div data-testid="admin-lead-detail" className="space-y-6">
      <Link
        to="/admin/leads"
        data-testid="lead-back"
        className="inline-flex items-center gap-2 text-sm font-semibold text-forest"
      >
        <ArrowLeft className="h-4 w-4" /> Back to leads
      </Link>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <header className="rounded-2xl bg-white border border-border-neutral p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-mint">
                  {lead.sourceLabel}
                </p>
                <h1 className="mt-1 text-2xl md:text-3xl font-extrabold text-forest">
                  {lead.firstName} {lead.lastName}
                </h1>
                {lead.businessName && (
                  <p className="mt-1 text-mid-neutral flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5" /> {lead.businessName}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <StagePill stage={lead.stage} />
                {lead.clientId ? (
                  <Link
                    to={`/admin/clients/${lead.clientId}`}
                    data-testid="lead-view-client"
                    className="inline-flex items-center gap-2 rounded-lg bg-lime text-forest px-4 py-2 text-sm font-bold"
                  >
                    <Briefcase className="h-4 w-4" /> View client
                  </Link>
                ) : lead.stage === "Won" ? (
                  <button
                    type="button"
                    onClick={convertToClient}
                    disabled={converting}
                    data-testid="lead-convert-to-client"
                    className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
                  >
                    {converting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Briefcase className="h-4 w-4" />}
                    Convert to Client
                  </button>
                ) : null}
              </div>
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
              <ContactRow Icon={Mail} value={lead.email} link={`mailto:${lead.email}`} />
              <ContactRow Icon={Phone} value={lead.phone} link={`tel:${lead.phone}`} />
              {lead.slotLabel && (
                <ContactRow Icon={CalendarCheck} value={lead.slotLabel} />
              )}
              {lead.pilot && (
                <ContactRow Icon={Tag} value={lead.pilot} />
              )}
            </div>

            {lead.products && lead.products.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-mid-neutral">Products</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {lead.products.map((p) => (
                    <span key={p} className="rounded-full bg-soft text-forest text-xs px-3 py-1 font-semibold">
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {lead.notes && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-mid-neutral">Notes from prospect</p>
                <p className="mt-2 text-sm text-forest whitespace-pre-wrap leading-relaxed">{lead.notes}</p>
              </div>
            )}
          </header>

          {/* Activity timeline */}
          <section className="rounded-2xl bg-white border border-border-neutral p-6">
            <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
              <Activity className="h-4 w-4 text-mint" /> Activity
            </h2>

            <form onSubmit={addNote} className="mt-4">
              <textarea
                data-testid="lead-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Add a note or call log…"
                className="w-full p-3 rounded-xl bg-soft border border-transparent focus:border-mint focus:outline-none text-sm text-forest"
              />
              <div className="mt-2 flex justify-end">
                <button
                  type="submit"
                  disabled={!note.trim() || posting}
                  data-testid="lead-note-submit"
                  className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
                >
                  {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Add note
                </button>
              </div>
            </form>

            <div className="mt-5 space-y-4">
              {(lead.timeline || []).length === 0 && (
                <p className="text-sm text-mid-neutral text-center py-4">
                  No activity yet. Add a note to start tracking follow-ups.
                </p>
              )}
              {(lead.timeline || []).map((t) => (
                <div key={t.id} className="flex gap-3">
                  <div className="mt-1">
                    <div className={
                      "h-8 w-8 rounded-full inline-flex items-center justify-center " +
                      (t.type === "stage-change" ? "bg-lime text-forest" : "bg-forest text-lime")
                    }>
                      {t.type === "stage-change" ? <Tag className="h-3.5 w-3.5" /> : <MessageSquare className="h-3.5 w-3.5" />}
                    </div>
                  </div>
                  <div className="flex-1 rounded-xl bg-soft/40 p-3">
                    <p className="text-xs text-mid-neutral">
                      <strong className="text-forest">{t.authorName || t.author}</strong> · {formatTs(t.createdAt)}
                    </p>
                    <p className="mt-1 text-sm text-forest whitespace-pre-wrap">{t.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <TasksSection relatedType="lead" relatedId={lead.id} />

          <SmsSection relatedType="lead" relatedId={lead.id} phone={lead.phone} />
        </div>

        {/* Sidebar */}
        <aside className="space-y-5">
          <div className="rounded-2xl bg-white border border-border-neutral p-5">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-mid-neutral">Stage</p>
            <div className="mt-3 space-y-1.5">
              {stages.map((s) => (
                <button
                  key={s}
                  type="button"
                  disabled={stageSaving || s === lead.stage}
                  onClick={() => changeStage(s)}
                  data-testid={`lead-stage-${s.toLowerCase().replace(/\s+/g,'-')}`}
                  className={
                    "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors " +
                    (s === lead.stage
                      ? "bg-forest text-lime font-bold cursor-default"
                      : "bg-soft hover:bg-soft/60 text-forest font-semibold")
                  }
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-border-neutral p-5">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-mid-neutral">Meta</p>
            <dl className="mt-3 text-xs text-forest space-y-2">
              <div>
                <dt className="text-mid-neutral">Received</dt>
                <dd className="font-semibold">{formatTs(lead.createdAt)}</dd>
              </div>
              {lead.utm?.source && (
                <div>
                  <dt className="text-mid-neutral">UTM source</dt>
                  <dd className="font-semibold">{lead.utm.source}</dd>
                </div>
              )}
              {lead.utm?.campaign && (
                <div>
                  <dt className="text-mid-neutral">UTM campaign</dt>
                  <dd className="font-semibold">{lead.utm.campaign}</dd>
                </div>
              )}
              {lead.ownerEmail && (
                <div>
                  <dt className="text-mid-neutral">Owner</dt>
                  <dd className="font-semibold flex items-center gap-1"><UserIcon className="h-3 w-3" />{lead.ownerEmail}</dd>
                </div>
              )}
            </dl>
          </div>
        </aside>
      </div>
    </div>
  );
}

function ContactRow({ Icon, value, link }) {
  if (!value) return null;
  const content = (
    <span className="inline-flex items-center gap-2 text-forest">
      <Icon className="h-3.5 w-3.5 text-mint shrink-0" />
      <span className="truncate">{value}</span>
    </span>
  );
  return link ? <a href={link} className="hover:underline">{content}</a> : content;
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "numeric", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
